import {
  DeleteProjectsMembersByProjectIdAndMemberIdApiArg,
  PutProjectsByProjectIdApiArg,
  PutProjectsMembersByProjectIdAndMemberIdApiArg,
} from "infra/api/generated/api";
import { apiSlice, SelectedInvalidatedByTag } from "infra/store/apiSlice";
import { API_TAGS } from "infra/store/constants";
import { Endpoints } from "infra/store/interface";
import _ from "lodash";

export const TEAMS_FIXED_KEYS = {
  deleteTeamMember: "deleteTeamMember",
  postTeamMember: "postTeamMember",
  postProjectMember: "postProjectMember",
};

export const teamsEndpoints: Endpoints = {
  getTeams: { providesTags: [API_TAGS.team] },
  postTeams: { invalidatesTags: [API_TAGS.team] },

  putTeamsByTeamId: {
    onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
      const { data } = await queryFulfilled;
      if (data?.team) {
        for (const {
          endpointName,
          originalArgs,
        } of apiSlice.util.selectInvalidatedBy(getState(), [
          API_TAGS.team,
        ]) as SelectedInvalidatedByTag[]) {
          if (
            endpointName === "getTeams" ||
            (endpointName === "getTeamsByTeamId" &&
              originalArgs.teamId === data.team.id)
          ) {
            dispatch(
              apiSlice.util.updateQueryData(
                endpointName,
                originalArgs,
                (draft) => {
                  if (!draft) {
                    return;
                  }

                  if (endpointName === "getTeams" && "teams" in draft) {
                    const teamIndex = draft.teams.findIndex(
                      (item) => item.id === data.team.id
                    );
                    if (teamIndex !== -1) {
                      draft.teams[teamIndex] = {
                        ...draft.teams[teamIndex],
                        ...arg.body,
                      };
                    }
                  } else if (
                    endpointName === "getTeamsByTeamId" &&
                    "team" in draft
                  ) {
                    draft.team = { ...draft.team, ...arg.body };
                  }
                }
              )
            );
          }
        }
      }
    },
  },
  getTeamsByTeamId: { providesTags: [API_TAGS.team] },
  deleteTeamsByTeamId: { invalidatesTags: [API_TAGS.team] },

  postTeamsMembersByTeamId: {
    invalidatesTags: [API_TAGS.team, API_TAGS.limits],
  },
  putTeamsMembersByTeamIdAndMemberId: {
    invalidatesTags: [API_TAGS.team, API_TAGS.limits],
  },
  deleteTeamsMembersByTeamIdAndMemberId: {
    invalidatesTags: [API_TAGS.team, API_TAGS.limits],
  },

  postTeamsApiKeysByTeamId: { invalidatesTags: [API_TAGS.team] },
  putTeamsApiKeyByTeamIdAndKeyId: { invalidatesTags: [API_TAGS.team] },
  deleteTeamsApiKeyByTeamIdAndKeyId: { invalidatesTags: [API_TAGS.team] },

  deleteTeamsInvitationsByTeamId: {
    invalidatesTags: [API_TAGS.team, API_TAGS.limits],
  },

  getProjects: { providesTags: [API_TAGS.project] },
  postProject: { invalidatesTags: [API_TAGS.project] },
  deleteProjectsByProjectId: { invalidatesTags: [API_TAGS.project] },

  putProjectsByProjectId: {
    onQueryStarted: handleProjectUpdateQueryStarted,
  },
  getProjectsByProjectId: { providesTags: [API_TAGS.project] },

  putProjectsMembersByProjectIdAndMemberId: {
    onQueryStarted: handleProjectUpdateQueryStarted,
  },
  deleteProjectsMembersByProjectIdAndMemberId: {
    onQueryStarted: handleProjectUpdateQueryStarted,
  },

  postProjectsApiKeysByProjectId: {
    invalidatesTags: [API_TAGS.project, API_TAGS.team],
  },
  deleteProjectsApiKeyByProjectIdAndKeyId: {
    invalidatesTags: [API_TAGS.project, API_TAGS.team],
  },

  getUsages: {
    merge(existing, incoming) {
      existing.hasMoreActivity =
        incoming.activity &&
        incoming.activity.length % 100 === 0 &&
        incoming.activity.length > 0;
      existing.activity = _.union(existing.activity, incoming.activity);
      existing.usages = _.union(existing.usages, incoming.usages);
      existing.consumption = _.union(
        existing.consumption,
        incoming.consumption
      );
    },
    serializeQueryArgs: ({ endpointName, queryArgs }) => {
      return (
        endpointName +
        queryArgs.projectIds +
        queryArgs.startDate +
        queryArgs.endDate +
        queryArgs.timeRange +
        queryArgs.userIds +
        queryArgs.teamId
      );
    },
    forceRefetch({ currentArg, previousArg }) {
      return !_.isEqual(currentArg, previousArg);
    },
  },
};

// ------------------------------------

async function handleProjectUpdateQueryStarted(
  arg:
    | PutProjectsByProjectIdApiArg
    | PutProjectsMembersByProjectIdAndMemberIdApiArg
    | DeleteProjectsMembersByProjectIdAndMemberIdApiArg,
  { dispatch, queryFulfilled, getState }: any
) {
  const { data } = await queryFulfilled;
  if (data?.project) {
    for (const {
      endpointName,
      originalArgs,
    } of apiSlice.util.selectInvalidatedBy(getState(), [
      API_TAGS.project,
    ]) as SelectedInvalidatedByTag[]) {
      if (
        endpointName === "getProjects" ||
        (endpointName === "getProjectsByProjectId" &&
          originalArgs.projectId === data.project.id)
      ) {
        dispatch(
          apiSlice.util.updateQueryData(endpointName, originalArgs, (draft) => {
            if (!draft) {
              return;
            }

            if (endpointName === "getProjects" && "projects" in draft) {
              const projectIndex = draft.projects.findIndex(
                (item) => item.id === data.project.id
              );
              if (projectIndex !== -1) {
                draft.projects[projectIndex] = {
                  ...data.project,
                };
              }
            } else if (
              endpointName === "getProjectsByProjectId" &&
              "project" in draft
            ) {
              draft.project = { ...data.project };
            }
          })
        );
      }
    }
  }
}
